<script setup lang="ts">
import { computed } from 'vue';
import RexTagV3 from '@/rex-components/RexTagV3.vue';
import {
  severityKeyMapper,
  anomalyTypeColorMapper,
  anomalyTagTextColor,
} from '@/components/charts/utils/utils.config';
import {
  AnomalyDataObjectType,
  GraphDataNamesEnum,
} from '@/components/Common/graph.types';
import { useI18n } from 'vue-i18n';
import { RexConfigProvider } from '@/rex-components/rex.comp.config';

interface Props {
  type: string;
  date: string;
  anomalyInfo: AnomalyDataObjectType;
}
const props = defineProps<Props>();
const { type, date, anomalyInfo } = props;
const { t } = useI18n();
const getAnomalyKey = computed(
  () => severityKeyMapper[props.anomalyInfo[GraphDataNamesEnum.SEVERITY]],
);
const getTagColor = computed(() => anomalyTypeColorMapper[getAnomalyKey.value]);
const getTagTextColor = computed(
  () => anomalyTagTextColor[getAnomalyKey.value],
);
const roundValues = (val) => Math.round(val * 1000) / 1000;
const getBreachInfo = computed(() => {
  return Number(anomalyInfo[GraphDataNamesEnum.UPPER_THRESHOLD]) <
    (Number(anomalyInfo.y) || 0)
    ? {
        header: t('upperBreach'),
        thresholdLine: `${t('upperThreshold')}: ${roundValues(anomalyInfo[GraphDataNamesEnum.UPPER_THRESHOLD])}`,
        groundTruth: `${t('groundTruth')}: ${roundValues(anomalyInfo.y)}`,
      }
    : {
        header: t('lowerBreach'),
        thresholdLine: `${t('lowerThreshold')}: ${roundValues(anomalyInfo[GraphDataNamesEnum.LOWER_THRESHOLD])}`,
        groundTruth: `${t('groundTruth')}: ${roundValues(anomalyInfo.y)}`,
      };
});
</script>

<template>
  <div class="anly-side-bar-item-box">
    <div class="anly-side-bar-tag-box">
      <RexConfigProvider
        :theme="{
          components: {
            Tag: {
              colorTextLightSolid: getTagTextColor,
            },
          },
        }"
      >
        <RexTagV3 :color="getTagColor" class="anly-side-bar-tag-text">
          {{ type }}
        </RexTagV3>
      </RexConfigProvider>
      <div class="anly-side-bar-text">
        {{ date }}
      </div>
    </div>

    <div class="anly-side-bar-text">
      <div class="anly-side-bar-breach-type">
        {{ getBreachInfo.header }}
      </div>
      <div class="anly-side-bar-item-breach-info">
        <div>
          {{ getBreachInfo.thresholdLine }}
        </div>
        <div>
          {{ getBreachInfo.groundTruth }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.anly-side-bar-item-box {
  height: 100%;
  padding: 0.75rem;
}
.anly-side-bar-tag-box {
  @include flex-center-justify(space-between);
  margin-bottom: 0.5rem;
}
.anly-side-bar-text {
  color: $font-primary;
  font-size: 0.85rem;
  line-height: 1rem;
}
.anly-side-bar-tag-text {
  font-weight: 600;
}
.anly-side-bar-breach-type {
  font-weight: 600;
  font-size: 1rem;
}
.anly-side-bar-item-breach-info {
  color: $table-header-text-grey;
  margin-top: 0.5rem;
}
</style>
